import { Link } from '@tanstack/react-router';
import { ErroreApi } from '../api/cliente.js';
import { useState } from 'react';
import {
  type RigaAnagrafica,
  type TipoAnagrafica,
  esporta,
  useAnagrafiche,
  useCancellaVista,
  useDefinizioneFiltri,
  useSalvaVista,
  useViste,
} from '../api/anagrafiche.js';
import { AzioniDiMassa } from '../componenti/AzioniDiMassa.js';
import { BarraFiltri } from '../componenti/BarraFiltri.js';
import { PiedeElenco } from '../componenti/PiedeElenco.js';
import { type Colonna, Tabella } from '../componenti/Tabella.js';
import { useRicerca } from '../useRicerca.js';
import { useSelezione } from '../useSelezione.js';
import { ModaleNuovaAnagrafica } from './ModaleNuovaAnagrafica.js';
import { locale, t } from '../i18n/lingua.js';

/**
 * L'elenco delle anagrafiche: la stessa pagina per clienti, fornitori,
 * agenti, lead, prospect e operatori.
 *
 * Rispetto al v2 cambiano quattro cose. La paginazione la decide il servizio,
 * quindi non c'è più un numero da indovinare. La ricerca è una casella sola
 * che cerca dove serve. I filtri si aggiungono uno alla volta invece di
 * comparire tutti insieme. E tutto sta nell'indirizzo del browser, quindi una
 * ricerca si può mandare a un collega con un link.
 */

/**
 * Le colonne dipendono dal percorso dell'elenco, perché il nome è un
 * collegamento alla scheda: da `/customers` si va a `/customers/1270`.
 */
const colonne = (percorso: string): Colonna<RigaAnagrafica>[] => [
  {
    chiave: 'name',
    titolo: t('Ragione sociale'),
    ordinabile: true,
    cella: (r) => (
      <>
        {/* i percorsi nascono da un elenco, quindi il router non può
            dedurne il tipo: l'indirizzo lo componiamo noi */}
        <Link className="cell-link strong" to={`${percorso}/${r.id}` as never}>
          {r.name}
        </Link>
        {r.archived && (
          <span className="badge" style={{ marginLeft: 8 }}>
            {t('archiviata')}
          </span>
        )}
      </>
    ),
  },
  { chiave: 'bcCodeSpa', titolo: t('Codice BC — SPA'), ordinabile: true, monospazio: true },
  { chiave: 'bcCodeInc', titolo: t('Codice BC — Inc'), ordinabile: true, monospazio: true },
  { chiave: 'taxNumber', titolo: t('Partita IVA'), ordinabile: true, monospazio: true },
  {
    chiave: 'city',
    titolo: t('Città'),
    // Cercando per paese o città può corrispondere una destinazione invece
    // della sede: l'elenco mostra quella e lo dichiara, altrimenti la riga
    // sembra finita lì per sbaglio.
    cella: (r) =>
      r.city ? (
        <>
          {r.city}
          {r.otherLocation && (
            <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>
              {t('· destinazione')}
            </span>
          )}
        </>
      ) : (
        <span className="dim">—</span>
      ),
  },
  { chiave: 'country', titolo: t('Paese') },
  { chiave: 'sectional', titolo: t('Sezionale'), monospazio: true },
  { chiave: 'currency', titolo: t('Valuta'), monospazio: true },
];

export function Anagrafiche({
  tipo,
  titolo,
  percorso,
}: {
  tipo: TipoAnagrafica;
  titolo: string;
  percorso: string;
}) {
  const COLONNE = colonne(percorso);
  const { ricerca, testo, setTesto, cambia, vaiA, ordinaPer, filtra, applica } = useRicerca();
  const [nomeVista, setNomeVista] = useState('');
  const [salvataggioAperto, setSalvataggioAperto] = useState(false);
  const [nuovaAperta, setNuovaAperta] = useState(false);

  const elenco = useAnagrafiche(tipo, ricerca);
  const [selezione, setSelezione] = useSelezione(elenco.data?.rows);
  const viste = useViste(tipo);
  const definizioni = useDefinizioneFiltri();
  const salvaVista = useSalvaVista(tipo);
  const cancellaVista = useCancellaVista(tipo);

  const salva = () => {
    const nome = nomeVista.trim();
    if (!nome) return;
    salvaVista.mutate(
      { name: nome, definition: { ...ricerca, page: 1 } },
      {
        onSuccess: () => {
          setNomeVista('');
          setSalvataggioAperto(false);
        },
      },
    );
  };

  const dati = elenco.data;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{titolo}</h1>
          <div className="page-sub">
            {dati
              ? ricerca.archived
                ? t('{n} in archivio, archiviate comprese', { n: dati.total.toLocaleString(locale()) })
                : t('{n} in archivio', { n: dati.total.toLocaleString(locale()) })
              : elenco.error
                ? t('Elenco non disponibile')
                : t('Un momento…')}
          </div>
        </div>
        <div className="page-actions">
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => esporta(tipo, ricerca, 'csv')}>
            CSV
          </button>
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => esporta(tipo, ricerca, 'xlsx')}>
            Excel
          </button>
          <button className="btn btn-primary" onClick={() => setNuovaAperta(true)}>
            {t('Nuovo')}
          </button>
        </div>
      </div>

      <div className="filterbar">
        <div className="presets">
          {viste.data?.views.map((vista) => (
            <span key={vista.id} className="preset-con-x">
              <button
                className="preset"
                onClick={() => applica(vista.definition)}
                title={vista.mine ? t('Vista tua') : t('Condivisa da un collega')}
              >
                {vista.name}
              </button>
              {vista.mine && (
                // un pulsante suo, accanto a quello della vista: dentro l'altro
                // pulsante, dalla tastiera non ci si arrivava
                <button
                  className="chip-x preset-x"
                  aria-label={t('Elimina la vista {nome}', { nome: vista.name })}
                  onClick={() => cancellaVista.mutate(vista.id)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {salvataggioAperto ? (
            <span className="row" style={{ gap: 4 }}>
              <input
                className="input"
                style={{ height: 26, width: 170 }}
                placeholder={t('Nome della vista')}
                // il servizio ne accetta 60: oltre, la vista non si salvava e niente lo diceva
                maxLength={60}
                value={nomeVista}
                autoFocus
                onChange={(e) => setNomeVista(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') salva();
                  if (e.key === 'Escape') setSalvataggioAperto(false);
                }}
              />
              <button className="btn" onClick={salva} disabled={salvaVista.isPending}>
                {t('Salva')}
              </button>
              {salvaVista.error instanceof ErroreApi && (
                <span className="hint" style={{ color: 'var(--tomato)' }}>
                  {salvaVista.error.message}
                </span>
              )}
            </span>
          ) : (
            <button
              className="preset btn-ghost"
              style={{ fontSize: 12 }}
              onClick={() => setSalvataggioAperto(true)}
            >
              {t('+ Salva questa vista')}
            </button>
          )}
        </div>

        <span className="omnisearch" style={{ maxWidth: 320, flex: '0 1 320px' }}>
          <span>⌕</span>
          <input
            className="input"
            style={{ border: 'none', height: 22, padding: 0, background: 'transparent' }}
            placeholder={t('Cerca per nome, codice BC o partita IVA')}
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
          />
        </span>

        <button
          className={ricerca.archived ? 'chip chip-set' : 'chip'}
          onClick={() => cambia({ archived: !ricerca.archived })}
        >
          {ricerca.archived ? t('Con le archiviate') : 'Mostra archiviate'}
        </button>

        <BarraFiltri
          definizioni={definizioni.data?.filters ?? []}
          opzioni={definizioni.data?.options ?? {}}
          valori={ricerca.filters}
          onCambia={filtra}
        />
      </div>

      <AzioniDiMassa
        percorso="/api/identities/bulk"
        nome="anagrafiche"
        selezione={selezione}
        conRipristina={ricerca.archived}
        chiaviDaRileggere={['anagrafiche']}
        onFatto={() => setSelezione(new Set())}
        onAnnulla={() => setSelezione(new Set())}
      />

      <div className="table-wrap" style={{ marginTop: 12, marginBottom: 24 }}>
        <Tabella
          colonne={COLONNE}
          righe={dati?.rows ?? []}
          chiaveRiga={(r) => r.id}
          ordina={ricerca.sort}
          verso={ricerca.dir}
          onOrdina={ordinaPer}
          selezione={selezione}
          onSelezione={setSelezione}
          caricamento={elenco.isFetching}
          errore={elenco.error}
          vuoto={
            ricerca.q ? t('Nessun risultato per «{cerca}».', { cerca: ricerca.q }) : t('Non c’è ancora niente qui.')
          }
        />
        <PiedeElenco
          pagina={dati?.page ?? 1}
          pagine={dati?.pages ?? 1}
          totale={dati?.total ?? 0}
          dimensione={ricerca.size}
          nomePlurale={titolo.toLowerCase()}
          onPagina={vaiA}
          onDimensione={(size) => cambia({ size })}
        />
      </div>

      {nuovaAperta && (
        <ModaleNuovaAnagrafica
          tipo={tipo}
          titolo={singolare(titolo)}
          onChiudi={() => setNuovaAperta(false)}
        />
      )}
    </>
  );
}

/**
 * «Clienti» → «cliente». Le sei voci del menu sono al plurale perché è così
 * che si chiamano gli elenchi, ma il titolo della finestra parla di uno solo.
 */
function singolare(titolo: string): string {
  const forme: Record<string, string> = {
    Clienti: 'Cliente',
    Fornitori: 'Fornitore',
    Agenti: 'Agente',
    Lead: 'Lead',
    Prospect: 'Prospect',
    Operatori: 'Operatore',
  };
  return forme[titolo] ?? titolo;
}
